// Global
import { ImageField, LinkField } from '@sitecore-content-sdk/nextjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { tv } from 'tailwind-variants';

// Local
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import useClickOutside from 'lib/hooks/useClickOutside';
import { useIsScrolled } from 'lib/hooks/useIsScrolled';
import { Data } from '.generated/Foundation.CW.model';
import { SiteStructure } from '.generated/Feature.CW.model';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import classNames from 'classnames';
import useIsEditing from 'lib/hooks/useIsEditing';
import useDictionary from 'lib/hooks/useDictionary';
import React from 'react';
import { SearchOverlay } from './SearchOverlay';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
// import PreviewSearchListComponent from 'widgets/SearchPreview';

const chunkLinks = (links: Data.Links.GenericLink_Item[], size: number) => {
  const chunks = [];
  for (let i = 0; i < links.length; i += size) {
    chunks.push(links.slice(i, i + size));
  }
  return chunks;
};

const DropdownMenu = ({
  categories,
  megaMenuImage,
  close,
}: {
  categories: SiteStructure.Header.MegaMenuGroup_Item[];
  megaMenuImage: ImageField | undefined;
  close?: () => void;
}) => {
  const isScrolled = useIsScrolled();
  const {
    dropDownMenuCol,
    dropDownMenuColHeading,
    dropDownMenuColItems,
    dropDownMenuColItemsList,
    dropDownMenuColItemsLink,
    dropDownMenuGrid,
    dropDownMenuInner,
    dropDownMenuSection,
    dropDownMenuWrapper,
  } = TAILWIND_VARIANTS({
    isScrolled: isScrolled,
  });
  const isEditing = useIsEditing();

  const handleOnBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // Close dropdown if focus shifts outside of the dropdown menu.
    if (!event.currentTarget.contains(event.relatedTarget)) {
      if (close !== undefined) close();
    }
  };

  return (
    <div className={dropDownMenuWrapper()} onBlur={handleOnBlur}>
      <div className={dropDownMenuInner()}>
        <div className={dropDownMenuSection()}>
          <div className={dropDownMenuGrid()}>
            {categories.map((category, index) => {
              const megaMenuLinks = category.fields?.megaMenuLinks as Data.Links.GenericLink_Item[];
              const linkChunks = chunkLinks(megaMenuLinks, 4);
              return (
                <div
                  aria-labelledby={`secondary-menu-${index + 1}`}
                  className={dropDownMenuCol()}
                  key={category.id}
                  role="group"
                >
                  {(category.fields?.megaMenuTitle?.value !== '' || isEditing) && (
                    <h2
                      id={`secondary-menu-${index + 1}`}
                      className={classNames(dropDownMenuColHeading(), 'font-centuryGothic')}
                    >
                      <PlainTextWrapper field={category.fields?.megaMenuTitle} />
                    </h2>
                  )}

                  <div className="flex justify-between flex-wrap gap-4">
                    {linkChunks.map((chunk, chunkIndex) => (
                      <ul key={chunkIndex} className={dropDownMenuColItems()}>
                        {chunk.map((item) => (
                          <li className={dropDownMenuColItemsList()} key={item.id}>
                            <LinkWrapper
                              className={classNames(
                                dropDownMenuColItemsLink(),
                                'font-centuryGothic',
                                '[&>svg]:hidden'
                              )}
                              field={item.fields?.link}
                              onClick={close}
                              prefetch={false}
                            />
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="ml-auto">
              <ImageWrapper field={megaMenuImage} className="rounded-md" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Logo = ({ logo, logoLink }: { logo?: ImageField; logoLink?: LinkField }) => {
  const { logoContainer } = TAILWIND_VARIANTS();

  return (
    <div className={logoContainer()}>
      <LinkWrapper field={logoLink?.value}>
        <ImageWrapper field={logo} />
      </LinkWrapper>
    </div>
  );
};

interface NavItemInterface extends SiteStructure.Header.PrimaryNavigationGroup_Item {
  dropdownOpen: number | null;
  index: number;
  open: () => void;
  close: () => void;
}

const NavItem = (props: NavItemInterface) => {
  const { dropdownOpen, index } = props || {};

  const megaMenuList = props?.fields?.megaMenuList as SiteStructure.Header.MegaMenuGroup_Item[];
  const megaMenuImage = props?.fields?.image;
  const navigationLink = props?.fields?.navigationLink;
  const navigationTitle = props?.fields?.navigationTitle;

  const isList = megaMenuList.length > 0;
  const navigationLinks = navigationLink?.value?.href;

  const router = useRouter();

  const asPath = router.asPath;

  /*
   * State
   */

  const [isActive, setIsActive] = useState(false);

  /*
   * EVENT HANDLERS
   */

  /*
   * Lifecycle
   */

  useEffect(() => {
    if (isList) {
      const hrefValues = megaMenuList
        .flatMap((category) => category.fields?.megaMenuLinks as Data.Links.GenericLink_Item[])
        .map((linkItem) => linkItem.fields?.link?.value.href)
        .filter((href) => href);
      setIsActive(hrefValues.includes(asPath));
    } else {
      setIsActive(asPath === navigationLinks);
    }
  }, [isList, megaMenuList, navigationLinks, asPath]);

  /*
   * Rendering
   */

  const {
    buttonItem,
    buttonItemIcon,
    buttonItemSublink,
    navTitleLinkWrapper,
    // navAnimation,
    // navActiveStrokes,
  } = TAILWIND_VARIANTS({
    isActive: isActive,
    isRotated: isList && index === dropdownOpen,
  });

  return (
    <li>
      {isList ? (
        <>
          <button
            aria-haspopup="true"
            aria-expanded={dropdownOpen === index}
            className={buttonItem()}
            onClick={() => isList && props.open()}
          >
            {/* <span className={navAnimation()} /> */}
            <span className={buttonItemSublink()}>
              <PlainTextWrapper field={navigationTitle} />
              <SvgIcon
                className={buttonItemIcon()}
                fill="none"
                icon="chevron-up"
                size="md"
                viewBox="0 0 24 24"
              />
            </span>
          </button>
          <div className={`${index === dropdownOpen ? 'block' : 'hidden'}`}>
            <DropdownMenu
              categories={megaMenuList}
              megaMenuImage={megaMenuImage}
              close={props.close}
            />
          </div>
          {/* {index === dropdownOpen ? <DropdownMenu categories={megaMenuList} /> : null} */}
        </>
      ) : (
        <LinkWrapper
          aria-haspopup="false"
          className={navTitleLinkWrapper()}
          ctaVariant="link"
          field={navigationLink}
          prefetch={false}
        >
          {/* <span className={isActive ? navActiveStrokes() : navAnimation()} /> */}
          <PlainTextWrapper field={navigationTitle} />
        </LinkWrapper>
      )}
    </li>
  );
};

const HeaderDesktop = (props: SiteStructure.Header.Header_Component) => {
  const { fields } = props || {};
  const { logo, logoLink } = fields || {};
  const router = useRouter();
  const searchRef = useRef<HTMLDivElement>(null);
  const { page } = useSitecore();
  const siteName = page?.siteName?.toLowerCase();
  const navigationList =
    fields?.primaryNavigationList as SiteStructure.Header.PrimaryNavigationGroup_Item[];

  const utilityNavList =
    fields?.utilityNavigationList as SiteStructure.Header.UtilityNavigation_Item[];

  const headerRef = useRef<HTMLDivElement>(null);

  /*
   * STATE
   */
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [showSearch, setShowSearch] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);

  const isScrolled = useIsScrolled();

  const isDropdownOpen = dropdownOpen !== null || showSearch;

  const { getDictionaryValue } = useDictionary();

  /*
   * EVENT HANDLERS
   */

  const handleSearchToggle = useCallback((isOpen: boolean) => {
    setIsSearchOpen(isOpen);
  }, []);

  const handleDropdownToggle = useCallback((index: number | null) => {
    setDropdownOpen((dropdownOpen) => (dropdownOpen === index ? null : index));
  }, []);

  const handleClickOutside = useCallback(() => {
    handleDropdownToggle(null);
    setShowSearch(false);
  }, [handleDropdownToggle]);

  useClickOutside(headerRef, isDropdownOpen, handleClickOutside);

  // handle close megamenu
  const handleCloseMegaMenu = () => {
    handleDropdownToggle(null);
    setShowSearch(false);
  };

  // Implemented esc keyboard close mega menu
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Escape' && document.activeElement?.tagName == 'BUTTON') {
        handleCloseMegaMenu();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      if (isSearchOpen && searchRef.current && !searchRef.current.contains(e.target as Node)) {
        handleSearchToggle(false);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [isSearchOpen, handleSearchToggle]);

  /*
   * Rendering
   */

  if (!fields) {
    return <></>;
  }

  const {
    //overlay,
    base,
    wrapper,
    //divider,
    inner,
    container,
    menuContainer,
    menuWrapper,
    menuItems,
    //languageWrapper,
    searchBox,
  } = TAILWIND_VARIANTS({
    isDropdownOpen: isDropdownOpen,
    isScrolled: isScrolled,
  });

  return (
    <>
      <div className={base()} id="header-desktop" ref={headerRef}>
        <div className={wrapper()}>
          <div className={inner()}>
            <div className={container()}>
              <div className={menuWrapper()}>
                <div className={menuContainer()}>
                  <Logo logo={logo} logoLink={logoLink} />
                </div>
                {/* will be hidden in private - start */}
                {router.query.private !== 'true' && (
                  <div className="flex flex-col print:hidden">
                    {/* utility nav links start*/}
                    <div>
                      <ul className="flex justify-end items-center gap-12 mb-9">
                        {utilityNavList?.map(
                          (item: SiteStructure.Header.NavLinks, index: number) => {
                            return (
                              item?.fields?.navigationLink?.value.href && (
                                <li className="py-7" key={index}>
                                  <LinkWrapper
                                    className="font-centuryGothic text-14 font-bold text-blue-base leading-[19.6px] hover:underline hover:underline-offset-2 hover:text-darkblue-base"
                                    field={item?.fields?.navigationLink}
                                    role="link"
                                    prefetch={false}
                                  >
                                    <PlainTextWrapper
                                      tag="span"
                                      field={item?.fields?.navigationTitle}
                                    />
                                  </LinkWrapper>
                                </li>
                              )
                            );
                          }
                        )}
                        <li className="theme-lightblue ml-12">
                          <LinkWrapper
                            ctaVariant="secondarySmall"
                            field={fields?.signIntoMyChart}
                            role="link"
                          />
                        </li>
                      </ul>
                    </div>
                    {/* utility nav links start*/}
                    <nav aria-label="Main Navigation">
                      <ul className={menuItems()}>
                        {navigationList?.map((item, index) => {
                          const isLastItem = index === navigationList.length - 1;

                          // For 'giving' site: last nav item styled as CTA
                          if (siteName === 'giving' && isLastItem) {
                            return (
                              <li className="theme-lightblue" key={item?.id}>
                                <LinkWrapper
                                  ctaVariant="primary"
                                  field={item?.fields?.navigationLink}
                                >
                                  <PlainTextWrapper field={item?.fields?.navigationTitle} />
                                </LinkWrapper>
                              </li>
                            );
                          }

                          // Default rendering for other items
                          return (
                            <NavItem
                              dropdownOpen={dropdownOpen}
                              index={index}
                              key={item?.id}
                              open={() => {
                                handleDropdownToggle(index);
                                setShowSearch(false);
                                setIsSearchOpen(false);
                              }}
                              close={handleCloseMegaMenu}
                              {...item}
                            />
                          );
                        })}

                        {siteName !== 'giving' && (
                          <li className="theme-lightblue">
                            {isSearchOpen ? (
                              <ButtonWrapper
                                ctaIconAlignment="left"
                                ctaIcon="close"
                                ctaVariant="secondary"
                                className="!px-7 !text-14 !bg-darkblue-50 !min-w-[119px] !border-none"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSearchToggle(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') {
                                    handleSearchToggle(false);
                                  }
                                }}
                              >
                                <PlainTextWrapper
                                  tag="span"
                                  field={{ value: getDictionaryValue('Close') }}
                                  className="!ml-4"
                                />
                              </ButtonWrapper>
                            ) : (
                              <ButtonWrapper
                                ctaIconAlignment="left"
                                ctaIcon="search"
                                ctaVariant="primary"
                                className="!px-7 !text-14 !min-w-[119px]"
                                onClick={() => handleSearchToggle(true)}
                              >
                                <PlainTextWrapper tag="span" field={fields?.searchPlaceholder} />
                              </ButtonWrapper>
                            )}
                            {isSearchOpen && (
                              <div ref={searchRef}>
                                <SearchOverlay handleSearchToggle={handleSearchToggle} />
                              </div>
                            )}
                          </li>
                        )}
                      </ul>
                    </nav>
                  </div>
                )}
                {/* will be hidden in private - end */}
              </div>
            </div>
          </div>
        </div>
        {showSearch && <div className={searchBox()}></div>}
      </div>
    </>
  );
};

export default HeaderDesktop;

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['font-centureyGothic', 'border-none'],
    buttonItem: [
      'text-18',
      'font-bold',
      'relative',
      'group',
      'px-0',
      'py-1',
      'text-blue-base',
      'focus:outline-theme-brand3',
      'after:content-[""]',
      'after:absolute',
      'after:bottom-0',
      'after:left-0',
      'after:w-full',
      'after:h-[3px]',
      'after:mt-[4px]',
      'after:rounded-[2px]',
      'after:transition-colors',
      'after:duration-300',
      'hover:after:bg-blue-base',
      'leading-[25px]',
    ],
    buttonItemIcon: ['duration-200', 'h-auto', 'trasition', '!w-xs'],
    buttonItemSublink: ['flex', 'gap-4', 'items-center'],
    container: ['max-w-screen-xxl', 'w-full', 'lg:px-20 px-10'],
    divider: ['h-3', 'opacity-100', '-mt-[2px]', 'bg-theme-black-800'],
    dropDownMenuCol: ['col-span-10', 'flex', 'flex-col', 'gap-4', 'text-start', 'xl:col-span-10'],
    dropDownMenuColHeading: [
      'font-bold',
      'text-16',
      'text-gray-900',
      'pb-4',
      'mb-8',
      'border-b-2',
      'border-b-gray-50',
    ],
    dropDownMenuColItems: ['flex', 'flex-col', 'list-none', 'min-w-[275px]', 'max-w-[290px]'],
    dropDownMenuColItemsList: ['py-7'],
    dropDownMenuColItemsLink: ['text-gray-700', 'hover:underline', 'font-bold', 'text-14'],
    dropDownMenuGrid: ['gap-x-[50px]', 'flex', 'flex-wrap'],
    dropDownMenuInner: ['bg-theme-black-400'],
    dropDownMenuSection: [
      'max-w-screen-xxl',
      'pb-[36px]',
      'pt-[32px]',
      'xl:mx-auto',
      'lg:px-20 px-10',
    ],
    dropDownMenuWrapper: [
      'absolute',
      'duration-200',
      'left-0',
      'overflow-hidden',
      'transition-all',
      'w-full',
      'z-10',
      'top-[158px]',
      'bg-white',
      'border-b-4',
      'border-blue-base',
    ],
    inner: ['flex', 'justify-center', 'px-4', 'py-7'],
    languageWrapper: ['flex', 'w-[28%]', 'lg:w-auto', 'items-center', 'justify-end'],
    logoContainer: ['flex', 'items-center', 'lg:w-[238px]', 'lg:h-[76px]', 'w-[162px]', 'h-[54px]'],
    menuContainer: ['flex', 'items-center'],
    menuItems: ['flex', 'items-center', 'md:gap-[20px]', 'xl:gap-[40px]', 'xl:pl-10'],
    menuWrapper: ['flex', 'justify-between'],
    navAnimation: [
      'absolute',
      'inset-x-0',
      'bottom-0',
      'h-0.5',
      'bg-black',
      'transform',
      'origin-left',
      'scale-x-0',
      'transition-transform',
      'duration-300',
      'ease-out',
      'group-hover:scale-x-100',
    ],
    navActiveStrokes: ['absolute', 'inset-x-0', 'bottom-0', 'h-0.5', 'bg-black'],
    navTitleLinkWrapper: [
      'font-bold',
      'relative',
      'group',
      'px-0',
      'py-1',
      'text-blue-base',
      'text-18',
      'leading-[25px]',
      'after:content-[""]',
      'after:absolute',
      'after:bottom-0',
      'after:left-0',
      'after:w-full',
      'after:h-[3px]',
      'after:mt-[4px]',
      'after:rounded-[2px]',
      'after:transition-colors',
      'after:duration-300',
      'hover:after:bg-blue-base',
      '[&>svg]:hidden',
    ],
    overlay: [
      'shadow-md',
      'before:backdrop-blur-sm',
      'before:bg-theme-black-800/50',
      'before:fixed',
      'before:h-full',
      'before:left-0',
      'before:top-0',
      'before:w-full',
      'before:z-10',
    ],
    searchBox: ['bg-white', 'float-right', 'p-3', 'shadow-', 'w-3/12'],
    wrapper: ['border-black', 'drop-shadow-global-nav', 'bg-white', 'z-20', 'relative'],
  },
  variants: {
    isActive: {
      false: {
        buttonItemIcon: ['fill-blue-base'],
        navActiveStrokes: ['scale-x-0'],
      },
      true: {
        buttonItemIcon: ['fill-blue-base'],
        navActiveStrokes: ['scale-x-100'],
      },
    },
    isDropdownOpen: {
      false: {
        wrapper: [],
      },
    },
    isRotated: {
      false: {
        buttonItem: ['after:bg-transparent'],
        navTitleLinkWrapper: ['after:bg-transparent'],
        buttonItemIcon: ['rotate-180'],
      },
      true: {
        buttonItem: ['after:bg-blue-base'],
        navTitleLinkWrapper: ['after:bg-blue-base'],
        buttonItemIcon: ['rotate-0'],
      },
    },
    isScrolled: {
      false: {
        inner: ['py-9'],
        divider: ['h-3'],
        dropDownMenuInner: [],
      },
      true: {
        inner: ['py-9'],
        divider: ['h-2'],
        dropDownMenuInner: [],
      },
    },
  },
});
